import { Activity, Target, Zap, Package, Landmark, TrendingUp, GitBranch, CheckCircle2 } from 'lucide-react';
import BriefThemeBar from '../BriefThemeBar';
import BriefCallout from '../BriefCallout';
import BriefDataTable from '../BriefDataTable';
import ProbabilityGrid from '../ProbabilityGrid';

function StockChip({ sym, onSelect }) {
  return (
    <button className="sm" onClick={() => onSelect?.(sym)}>
      <span className="sm-sym">{sym}</span>
    </button>
  );
}

function HCard({ barLabel, barStatus, sections }) {
  return (
    <div className="hcard">
      <div className="hcard-bar">
        <span className="hcard-issue">{barLabel}</span>
        <span className="hcard-live"><span className="live-dot" />{barStatus}</span>
      </div>
      <div className="hcard-body">
        {sections.map((sec, i) => (
          <div key={i}>
            <div className="hcard-sec">{sec.label}</div>
            {sec.rows.map((r, j) => (
              <div key={j} className="hcard-row" style={j === sec.rows.length - 1 ? { border: 'none' } : {}}>
                {r.chip && <button className="sm" onClick={() => r.onChipClick?.()}><span className="sm-sym">{r.chip}</span></button>}
                {r.sym && <span className="hcard-sym" style={{ width: 80 }}>{r.sym}</span>}
                <span className="hcard-sig">{r.signal}</span>
                {r.value && (
                  <span className={`hcard-chg${r.up ? ' up' : ''}`} style={!r.up ? { color: 'var(--muted)', fontFamily: 'var(--fm)' } : {}}>
                    {r.value}
                  </span>
                )}
                {r.badge && <span className={`badge b-hit`} style={{ marginLeft: 'auto' }}><CheckCircle2 size={9} />{r.badge}</span>}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function BriefView({ onSelectStock }) {
  const sel = sym => () => onSelectStock?.(sym);

  return (
    <>
      <BriefThemeBar
        session="pre"
        date="Monday, May 19, 2026 · 9:02 AM ET"
        headline="Memory sector squeeze building."
        accent="Samsung injunction breaks the logjam."
        issueNum={104}
        stockCount={3}
        sectionCount={5}
        confidence={98}
      />

      <div className="brief-scroll">

        {/* §01 Tape & Action */}
        <div className="brief-section">
          <div className="sec-tag"><Activity size={13} />§01 · Tape &amp; Action</div>
          <h2>Pre-market tape.<br /><em>Unchanged at the bell.</em></h2>
          <p className="section-lead">Since our earlier briefing, the pre-market tape remains unchanged as we are still minutes away from the opening bell.</p>
          <HCard
            barLabel="Pre-Market · 9:02 AM ET · No changes since prior briefing"
            barStatus="Awaiting open"
            sections={[{
              label: <><Activity size={12} /> Pre-Market Indications</>,
              rows: [
                { chip: 'SNDK', onChipClick: sel('SNDK'), signal: 'Pre-market indication locked · no structural change since last check-in', value: '$1,412.50' },
                { chip: 'MU',   onChipClick: sel('MU'),   signal: <><TrendingUp size={12} style={{ color: 'var(--green)' }} /> Samsung strike injunction relief · primary driver</>, value: '$738.10', up: true },
                { chip: 'INTC', onChipClick: sel('INTC'), signal: 'Unchanged from Friday close · awaiting opening volume', value: '$108.77' },
              ],
            }]}
          />
        </div>

        {/* §02 Valuation & Probability */}
        <div className="brief-section">
          <div className="sec-tag"><Target size={13} />§02 · Valuation &amp; Probability</div>
          <h2>55% bull squeeze.<br /><em>Primary scenario heading in.</em></h2>
          <p className="section-lead">Structural valuations and the intraday probability matrix remain identical heading into the open. High theta and elevated IV persist as the market prices in the new Fed leadership.</p>
          <ProbabilityGrid
            onSelectStock={onSelectStock}
            scenarios={[
              { variant: 'bull', label: 'Bull Squeeze', pct: 55, desc: '+5% to +8% squeeze in memory names this morning due to the breaking judicial developments in South Korea.', chips: ['SNDK', 'MU'] },
              { variant: 'base', label: 'Base — Fade & Hold', pct: 30, desc: 'Initial pop fades as market digests the partial scope of the injunction. Memory names hold but do not extend gains.', chips: ['SNDK', 'MU'] },
              { variant: 'bear', label: 'Bear — Reversal', pct: 15, desc: 'Union finds workaround. Supply concern unresolved. Memory names give back all pre-market gains at the open.', chips: ['INTC'] },
            ]}
          />
          <BriefDataTable rows={[
            { label: 'SNDK Valuation', value: 'Undervalued against $42B backlog', badge: { text: 'Bullish', color: 'green' } },
            { label: 'MU Forward P/E', value: '33× — in range', badge: { text: 'Neutral', color: 'amber' } },
            { label: 'INTC Valuation', value: 'Overvalued, technically brittle', badge: { text: 'Bearish', color: 'red' } },
            { label: 'Options Flow', value: 'High theta, elevated IV — market pricing new Fed leadership transition', badge: { text: 'Watch', color: 'amber' } },
          ]} />
        </div>

        {/* §03 Pipelines & Competition */}
        <div className="brief-section">
          <div className="sec-tag"><Zap size={13} />§03 · Pipelines &amp; Competition</div>
          <h2>Samsung injunction.<br /><em>The mechanics, verified.</em></h2>
          <p className="section-lead">While the baseline demand pipelines are unchanged, we now have critical, newly verified details regarding the mechanics of the Samsung supply threat.</p>
          <BriefCallout title="Breaking — Samsung Strike · Suwon District Court" icon={Zap}>
            <p>The <strong>Suwon District Court partially accepted</strong> the April 16 injunction request filed by Samsung Electronics. The ruling explicitly ordered the labor union not to interfere with the operation of safety protection facilities or work needed to prevent facility damage and wafer deterioration.</p>
            <p>The union had announced an 18-day full-scale strike with ~50,000 participants. The core deadlock: removal of the 50% performance bonus cap and institutionalization of a plan allocating 15% of operating profit to fund payments.</p>
            <p><strong>What to watch</strong>: Whether the union files an appeal. A successful appeal would restore the bear scenario for SNDK and MU and flip the probability matrix entirely.</p>
          </BriefCallout>
          <div style={{ marginTop: 12 }}>
            <HCard
              barLabel="Pipeline Update · No new structural changes"
              barStatus="On track"
              sections={[{
                label: <><GitBranch size={12} /> Development Pipelines</>,
                rows: [
                  { sym: 'SK Hynix', signal: <><CheckCircle2 size={12} style={{ color: 'var(--green)' }} /> Development pipeline on track · no new updates</>, badge: 'On track' },
                  { sym: 'Google TurboQuant', signal: <><CheckCircle2 size={12} style={{ color: 'var(--green)' }} /> Adoption pipeline on track · no structural updates this morning</>, badge: 'On track' },
                ],
              }]}
            />
          </div>
        </div>

        {/* §04 Supply Chain */}
        <div className="brief-section">
          <div className="sec-tag"><Package size={13} />§04 · Supply Chain</div>
          <h2>Grid status:<br /><em>Green.</em></h2>
          <p className="section-lead">Base-load capacity remains normal across core global fabrication nodes. Spot pricing will update after global commodity markets close today.</p>
          <BriefDataTable rows={[
            { label: 'Helium Spot', value: '$60.96 / MC — Friday close · update pending until commodity markets close', badge: { text: 'Pending', color: 'amber' } },
            { label: 'Bromine', value: 'Supply data unchanged from earlier morning report', badge: { text: 'Pending', color: 'amber' } },
            { label: 'Global Power Grid', value: 'GREEN — Normal · Base-load normal across all fabrication nodes' },
          ]} />
        </div>

        {/* §05 Policy & Geopolitics */}
        <div className="brief-section">
          <div className="sec-tag"><Landmark size={13} />§05 · Policy &amp; Geopolitics</div>
          <h2>Fed chair confirmed.<br /><em>Warsh, 54–45.</em></h2>
          <p className="section-lead">Warsh is taking over a central bank actively wrestling with the economic fallout and inflation spikes from the war started by the U.S. and Israel with Iran on Feb. 28.</p>
          <BriefCallout title="Federal Reserve Transition — Senate Confirmed" icon={Landmark}>
            <p><strong>Kevin Warsh</strong> confirmed 54–45. While Powell's term as chair expired Friday, his Fed governor term does not expire until 2028 — he remains on the board indefinitely. Warsh has promised to sell all assets including stakes in Polymarket and SpaceX within 90 days.</p>
            <p>The April 8 ceasefire framework structurally holds, but the underlying <strong>inflationary damage from the initial Iran conflict remains the primary headwind for tech multiples</strong> — and is now Warsh's first problem to solve.</p>
          </BriefCallout>
          <div style={{ marginTop: 16 }}>
            <BriefDataTable rows={[
              { label: 'Fed Chair', value: 'Kevin Warsh — confirmed. Takes office this week.', badge: { text: 'Market moving', color: 'amber' } },
              { label: 'Operation Epic Fury', value: 'April 8 ceasefire holds. Inflationary damage remains primary headwind for tech multiples.', badge: { text: 'Risk active', color: 'red' } },
              { label: 'Helium Priority Act', value: 'No new executive announcements since prior briefing.', badge: { text: 'Monitoring', color: 'amber' } },
            ]} />
          </div>
        </div>

      </div>
    </>
  );
}
